/**
 * RPU Search Bar Component
 * Autocomplete search for RPU numbers and addresses using new unified API
 * 
 * Features:
 * - Debounced search (300ms)
 * - Inline autocomplete results
 * - Keyboard navigation
 * - Relevance-based sorting
 */

'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Search, MapPin, Loader2, X } from 'lucide-react';
import { apiClient } from '../../services/api';

type ApprovalStatus = 'approved' | 'rejected' | 'pending';

interface RPUResult {
  rpu: string;
  address: string;
  latitude: number;
  longitude: number;
  cfe_address?: string;
  cfe_annual_kwh?: number | null;
  roof_ai?: {
    approval_status?: ApprovalStatus;
    approval_updated_by?: string;
    approval_updated_at?: string;
    [key: string]: any;
  };
}

// Approval badge config: label + Tailwind classes per status
const APPROVAL_BADGE_CONFIG: Record<ApprovalStatus, { label: string; className: string }> = {
  approved: { label: 'Aprobado', className: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rechazado', className: 'bg-red-100 text-red-700' },
  pending: { label: 'Pendiente', className: 'bg-yellow-100 text-yellow-700' },
};

function ApprovalBadge({ roofAi }: { roofAi?: RPUResult['roof_ai'] }) {
  const status: ApprovalStatus = roofAi?.approval_status ?? 'pending';
  const config = APPROVAL_BADGE_CONFIG[status] ?? APPROVAL_BADGE_CONFIG.pending;

  // Build tooltip only when audit info is present
  let title: string | undefined;
  if (roofAi?.approval_updated_by) {
    const verb = status === 'rejected' ? 'Rechazado' : 'Aprobado';
    const when = roofAi.approval_updated_at
      ? new Date(roofAi.approval_updated_at).toLocaleString('es-MX')
      : '';
    title = when
      ? `${verb} por ${roofAi.approval_updated_by} el ${when}`
      : `${verb} por ${roofAi.approval_updated_by}`;
  }

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.className}`}
      title={title}
    >
      {config.label}
    </span>
  );
}

interface RPUSearchBarProps {
  onSelectRPU: (result: RPUResult) => void;
  placeholder?: string;
  className?: string;
}

export default function RPUSearchBar({ 
  onSelectRPU, 
  placeholder = "Search RPU number...",
  className = ""
}: RPUSearchBarProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<RPUResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout>();

  // Debounced search function
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Don't search if query is too short
    if (query.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    // Set loading state
    setLoading(true);

    // Debounce search (300ms)
    debounceTimerRef.current = setTimeout(async () => {
      try {
        // Call new unified search endpoint using apiClient
        const response = await apiClient.get('/api/rpus/search', {
          params: {
            q: query,
            mode: 'inline',
            limit: 10
          }
        });

        const data = response.data;


        if (data.success) {
          setResults(data.results || []);
          setIsOpen(true); // Always show dropdown when we have a response
          setSelectedIndex(-1);
        } else {
          console.error('Search failed:', data.message);
          setResults([]);
          setIsOpen(false);
        }
      } catch (error) {
        console.error('RPU search error:', error);
        setResults([]);
        setIsOpen(false);
      } finally {
        setLoading(false);
      }
    }, 300);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [query]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleSelectResult(results[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Handle result selection
  const handleSelectResult = (result: RPUResult) => {
    setQuery(result.rpu);
    setIsOpen(false);
    setSelectedIndex(-1);
    onSelectRPU(result);
  };

  // Clear search
  const handleClear = () => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  return (
    <div className={`relative ${className}`}>
      {/* Search Input */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Search className="h-5 w-5" />
          )}
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          className="w-full pl-10 pr-10 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors font-medium text-sm"
        />
        
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Autocomplete Dropdown */}
      {isOpen && results.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-[9999] w-full mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-xl max-h-80 overflow-y-auto"
        >
          {results.map((result, index) => (
            <button
              key={`${result.rpu}-${index}`}
              onClick={() => handleSelectResult(result)}
              className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                index === selectedIndex ? 'bg-gray-50' : ''
              }`}
            >
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-bold text-black text-sm">
                      RPU: {result.rpu}
                    </div>
                    <ApprovalBadge roofAi={result.roof_ai} />
                  </div>
                  {result.address && (
                    <div className="text-xs text-gray-600 truncate">
                      {result.address}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No Results Message */}
      {isOpen && !loading && query.length >= 2 && results.length === 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-[9999] w-full mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-xl p-4"
        >
          <div className="text-center text-gray-500 text-sm">
            No results found for "{query}"
          </div>
        </div>
      )}
    </div>
  );
}

